import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { InputAdornment, Skeleton, Stack } from '@mui/material';
import classes from './MainInput.module.scss';
import { inputVariants } from './types';
import colors from '../../../../scss/colors';
import { CloseIcon } from '../../Icons';
import { responsiveSizeJobs } from '../../../../utils/constants';
import { useWindowSize } from '../../../../utils/hooks';

const textFieldStyles = (customBorder, customRadius, windowWidth) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    fontSize: windowWidth > responsiveSizeJobs ? '16px' : '12px',
    color: colors.BLACK_TXT,
    borderRadius: 0,
    paddingLeft: '18px',
    padding: windowWidth > responsiveSizeJobs ? '9px 9px 9px 18px' : '6px 6px 6px 18px'
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    ...customBorder
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    ...customBorder
  },
  '& .MuiAutocomplete-option': {
    padding: '6px 22px',
    fontSize: '14px'
  },
  '& .MuiAutocomplete-popper.MuiAutocomplete-popperDisablePortal': {
    top: windowWidth < responsiveSizeJobs ? '46px !important' : 'auto'
  },
  '& .MuiPopper-root.MuiAutocomplete-popper': {
    top: windowWidth < responsiveSizeJobs ? '46px !important' : 'auto'
  },
  '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
    fontSize: windowWidth < responsiveSizeJobs ? '12px' : '16px',
    paddingBottom: '20px',
    paddingLeft: '18px',
    paddingRight: '18px',
    minHeight: 0
  },
  '& .customPopper ul li': {
    padding: 0
  },
  '&.MuiAutocomplete-root .MuiInputBase-root.MuiOutlinedInput-root': {
    borderRadius: customRadius || 0
  },
  background: colors.WHITE,
  color: colors.GRAY_TXT,
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '17px',
  borderRadius: customRadius,
  fieldset: {
    ...customBorder,
    legend: {
      width: 'unset'
    }
  }
});

function MainInput({
  size = 'small',
  variant = inputVariants.PRIMARY,
  outlined = false,
  rounded = false,
  customRadius = false,
  customBorder = false,
  changeHandler,
  onKeyDownHandler,
  onInputHandler,
  forceWidth,
  disabled,
  type = 'text',
  placeholder,
  value,
  icon,
  containerSx,
  autoComplete,
  data = [],
  pendingData,
  searchObj,
  clearHandler,
  suggestionHandler,
  onFocusHandler,
  onBlurHandler,
  clearable,
  customPosition,
  inputRef,
  forceOpenOption,
  showClear,
  suggestions = false,
  multiLinePlaceholder
}) {
  const sizes = {
    small: classes.small,
    medium: classes.medium,
    large: classes.large
  };

  const roundedBySize = {
    small: '5px',
    medium: '10px',
    large: '20px'
  };

  const variants = {
    primary: {
      text: colors.GRAY_TXT,
      border: colors.GRAY_TXT,
      background: colors.WHITE
    }
  };

  const getVariants = (variantType) => {
    return {
      background: variants[variantType].background,
      color: variants[variantType].text,
      border: variants[variantType].border
    };
  };

  const getBorderRadius = () => {
    if (rounded) return roundedBySize[size];
    if (customRadius) return customRadius;

    return 0;
  };

  const { width } = useWindowSize();

  const [inputText, setInputText] = useState(value || '');

  useEffect(() => {
    setInputText(value || '');
  }, [value]);

  return (
    <div key={inputRef} className={classes.inputContainer} style={containerSx}>
      {!autoComplete ? (
        <>
          <div className={classes.iconContainer}>{icon && icon}</div>
          <input
            key="basic"
            ref={inputRef}
            type={type}
            className={`${classes.basic} ${sizes[size]}`}
            style={{
              ...getVariants(variant, outlined),
              borderRadius: getBorderRadius(),
              width: forceWidth || 'fit-content',
              ...customBorder
            }}
            disabled={disabled}
            value={value}
            onChange={changeHandler}
            onInput={onInputHandler}
            placeholder={placeholder}
          />
        </>
      ) : (
        <Autocomplete
          open={false}
          key="autocomplete"
          openOnFocus={forceOpenOption}
          freeSolo
          disablePortal
          options={suggestions ? data?.map((option) => option[searchObj]) : [] || []}
          sx={textFieldStyles(customBorder, customRadius, width)}
          onChange={(event, newValue) => (!value ? suggestionHandler(newValue) : changeHandler(newValue))}
          onInput={onInputHandler}
          onKeyDown={onKeyDownHandler}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          value={value}
          loading={suggestions ? pendingData : false}
          loadingText={
            <Stack flexDirection="row">
              <Skeleton key="sk1" animation="wave" width="50%" />
              &nbsp;
              <Skeleton key="sk2" animation="wave" width="50%" />
            </Stack>
          }
          renderOption={(props, option) => (
            <>
              {!value && (
                <li key="recent-search-label" className={classes.recentSearch}>
                  Recent Searches
                </li>
              )}
              <li className={classes.optionList} {...props} key={props.id}>
                {option}
              </li>
            </>
          )}
          renderInput={(params) => (
            <div style={{ position: 'relative' }}>
              <TextField
                key="advanced"
                inputRef={inputRef}
                {...params}
                placeholder={multiLinePlaceholder ? '' : placeholder}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
                  endAdornment: showClear && clearable && inputText?.length > 0 && (
                    <InputAdornment sx={{ cursor: 'pointer' }} onClick={clearHandler} position="end">
                      <CloseIcon />
                    </InputAdornment>
                  )
                }}
              />
              {multiLinePlaceholder && !inputText && (
                <span
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '54px',
                    transform: 'translateY(-50%)',
                    color: colors.GRAY_TXT,
                    fontSize: width > responsiveSizeJobs ? '15px' : '12px',
                    lineHeight: '1.4',
                    pointerEvents: 'none',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {placeholder}
                </span>
              )}
            </div>
          )}
          componentsProps={{
            popper: {
              sx: {
                top: customPosition
              }
            },
            paper: {
              sx: {
                boxShadow: '0px 35px 48px 0px #F6F7F9',
                fontSize: width > responsiveSizeJobs ? '14px' : '12px',
                '& .MuiAutocomplete-option': {
                  padding: width > responsiveSizeJobs ? '6px 22px !important' : '10px 18px !important'
                }
              },
              className: 'customPopper'
            }
          }}
        />
      )}
    </div>
  );
}

MainInput.propTypes = {
  changeHandler: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired
};

export default MainInput;
